import { memo } from 'react';
import type { OrderStatus } from '@/types';

interface OrderStatusTabsProps {
  activeTab: OrderStatus;
  onTabChange: (tab: OrderStatus) => void;
  orderCounts: Record<OrderStatus, number>;
  loading?: boolean;
}

const statusLabels: Record<OrderStatus, string> = {
  PENDING: '접수 대기',
  ACCEPTED: '처리 중', 
  READY: '처리 완료',
  COMPLETED: '완료',
  CANCELLED: '취소',
  NOSHOW: '노쇼'
};

export const OrderStatusTabs = memo(function OrderStatusTabs({ 
  activeTab, 
  onTabChange, 
  orderCounts,
  loading = false 
}: OrderStatusTabsProps) {
  // 표시할 탭들 (주요 3개)
  const mainTabs: OrderStatus[] = ['PENDING', 'ACCEPTED', 'READY'];
  
  // 처리 완료 탭의 경우 복합 카운트 계산
  const getTabCount = (status: OrderStatus): number => {
    if (status === 'READY') {
      // 처리 완료 탭은 READY, COMPLETED, CANCELLED, NOSHOW의 합계
      return (orderCounts['READY'] || 0) + 
             (orderCounts['COMPLETED'] || 0) + 
             (orderCounts['CANCELLED'] || 0) + 
             (orderCounts['NOSHOW'] || 0);
    }
    return orderCounts[status] || 0;
  };
  
  if (loading) {
    return (
      <div className="bg-gray-100 rounded-lg p-3 mb-4">
        <div className="flex space-x-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-10 bg-gray-200 rounded-lg w-20"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 rounded-lg p-3 mb-4">
      <div className="flex space-x-2">
        {mainTabs.map((status) => {
          const isActive = activeTab === status;
          const count = getTabCount(status);
          const isPending = status === 'PENDING';
          
          // 처리 완료 탭의 라벨 조정
          const label = status === 'READY' ? '완료/종료' : statusLabels[status];
          
          return (
            <button
              key={status}
              onClick={() => onTabChange(status)}
              className={`
                flex-1 py-2 px-3 rounded-lg font-medium text-sm transition-colors
                ${isActive 
                  ? 'bg-white text-gray-800 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-800'
                }
              `}
            >
              <div className="flex flex-col items-center space-y-1">
                <span>{label}</span>
                <span className={`
                  text-lg font-bold
                  ${isPending && count > 0 ? 'text-red-500' : 'text-gray-800'}
                `}>
                  {count}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
});